/* The establishment context — D-31 (05/09, ratified). One lens over every screen:
   « Tous les établissements » (the workspace) or one establishment. Lists filter,
   objects never fork; establishment-only objects inherit the context or ask first
   (D-32); the context is sticky, visible (the chip + the crumb) and deep-linkable
   (`?etablissement=<id>` on any route). Role landing (D-46): a manager lands in
   her establishment and cannot switch; owners and admins get « Tous ».
   Mock persistence per world, like the roues and the campaigns. */
import { useSyncExternalStore } from 'react'
import { currentWorld } from '@/mocks/worlds'
import { apiWrites, type Location } from './api'
import { edited, mirror, serverLocationId, setMirror, subscribeMirror, type ServerWorkspace } from './mirror'

export const ALL = 'all'

/* ---------- the current user (mock of Builder B's team data — D-46) ---------- */
export type Role = 'owner' | 'admin' | 'manager' | 'staff'
/* The four roles of the schema (0018, D-46): owner / admin are UNSCOPED — `locations` is ignored
   for them, never a restriction; manager / staff are scoped to their list, and an EMPTY list is
   nothing, not everything (batch 13 — the seam used to fall through to every establishment). */
export interface CurrentUser { role: Role; name: string; locations: string[] }
/** A role whose view is limited to `locations` (manager, staff). */
export const isScoped = (u: CurrentUser) => u.role === 'manager' || u.role === 'staff'
const userKey = () => `bmr.user.${currentWorld()}`
/** With an API the user's establishments are the server's: a demo « l0 » (the world switcher's manager) reads as its
 *  first establishment (serverLocationId), and an empty list stays nothing. */
export function currentUser(): CurrentUser {
  const u = storedUser()
  return apiWrites ? { ...u, locations: u.locations.map(serverLocationId) } : u
}
/** The user as this browser keeps it, ids untouched (lib-pack-18 rider): what the mock's GET /app/me answers, before the
 *  mirror holds any establishment — mapping there would turn a dialled server id into nothing. */
export function storedUser(): CurrentUser {
  try {
    const raw = localStorage.getItem(userKey())
    if (raw) { const u = JSON.parse(raw) as CurrentUser; if (u && u.role) return u }
  } catch { /* fine */ }
  return { role: 'owner', name: 'Lyam', locations: [] }
}
export function setCurrentUser(u: CurrentUser | null): void {
  try { if (u) localStorage.setItem(userKey(), JSON.stringify(u)); else localStorage.removeItem(userKey()) } catch { /* fine */ }
  notify()
}

/* ---------- the context ---------- */
const scopeKey = () => `bmr.etablissement.${currentWorld()}`
const listeners = new Set<() => void>()
const notify = () => listeners.forEach(l => l())

/** The context: `ALL` or an establishment id. `?etablissement=` on the URL wins and
 *  persists (a deep link lands in context); a manager is always inside her establishment. */
export function getScope(): string {
  const u = currentUser()
  if (isScoped(u) && u.locations.length) return u.locations[0]
  try {
    const q = new URLSearchParams(location.search).get('etablissement')
    if (q) {
      /* a deep link lands in context: persist it, then consume the parameter so the switcher stays in charge */
      try { if (q === ALL) localStorage.removeItem(scopeKey()); else localStorage.setItem(scopeKey(), q) } catch { /* fine */ }
      try { const url = new URL(location.href); url.searchParams.delete('etablissement'); history.replaceState(history.state, '', url.toString()) } catch { /* fine */ }
      return q === ALL ? ALL : q
    }
  } catch { /* no URL */ }
  try { return known(localStorage.getItem(scopeKey()) || ALL) } catch { return ALL }
}
/** With an API, a context the server does not know (a demo establishment kept in this browser) reads as « Tous ». */
const known = (id: string): string => !apiWrites || id === ALL || mirror().locations.some(l => l.id === id) ? id : ALL
export function setScope(id: string): void {
  try { if (id === ALL) localStorage.removeItem(scopeKey()); else localStorage.setItem(scopeKey(), id) } catch { /* fine */ }
  notify()
}
/* with an API the sector, the window and the identity fields are the mirror's: a screen hears the server's answer too */
const subscribe = (l: () => void) => { listeners.add(l); const off = subscribeMirror(l); return () => { listeners.delete(l); off() } }
/** Reactive read of the context — every filtered list uses it. */
export function useScope(): [string, (id: string) => void] {
  const s = useSyncExternalStore(subscribe, getScope, getScope)
  return [s, setScope]
}
/** Does an object that lives in these establishments belong to the context? */
export const inScope = (scope: string, locationIds: string[] | undefined) =>
  scope === ALL || !locationIds || locationIds.length === 0 || locationIds.includes(scope)
/** The establishments this user may see (D-46). */
export function allowedLocations(all: Location[]): Location[] {
  const u = currentUser()
  if (isScoped(u)) return all.filter(l => u.locations.includes(l.id))   // an empty list is nothing (0018)
  return all
}
/** A scoped user with no establishment at all: the espace is empty and says so. */
export const hasNoEstablishment = (all: Location[]) => isScoped(currentUser()) && allowedLocations(all).length === 0
/** The switcher exists only with two or more establishments to choose from (ON-18). */
export const canSwitch = (all: Location[]) => allowedLocations(all).length > 1
/** A route with the context carried along (M-12). */
export function scopedPath(path: string, scope: string): string {
  if (scope === ALL) return path
  return `${path}${path.includes('?') ? '&' : '?'}etablissement=${scope}`
}

/* ---------- the participation window — rule 3 of the Parcours, set in Établissement (batch 13) ----------
   « 1 par client / N jours » for the roues of an establishment. One value per establishment,
   ONE place to edit (the establishment's page); the Parcours' rhythm screen (RJ-07, Builder A)
   reads it and links here. The presets are RJ-07's business shapes; anything under the
   sector's own rhythm is shown and refused, never hidden (Bible §26). */
export interface FenetrePreset { id: string; days: number; label: string; sectors: string; plain: string }
export const FENETRE_PRESETS: FenetrePreset[] = [
  { id: 'tres-souvent', days: 1,  label: 'Très souvent', sectors: 'café, boulangerie', plain: 'une fois par jour' },
  { id: 'souvent',      days: 7,  label: 'Souvent',      sectors: 'restaurant, bar',   plain: 'une fois par semaine' },
  { id: 'regulier',     days: 30, label: 'Régulier',     sectors: 'salon, institut',   plain: 'une fois par mois' },
  { id: 'espace',       days: 60, label: 'Espacé',       sectors: 'garage, opticien',  plain: 'une fois tous les deux mois' },
]
/** The sector's rhythm — a salon's by default. The floor: shorter is refused. `sectorFloor()` reads the
    sector chosen on day one (D-66, batch 15); this constant stays the salon's value for readers that
    predate the sector (Builder A's Rythme). */
export const FENETRE_SECTEUR = 30
/* ---------- the sector (D-66) — chosen once on day one, the floor of the window and the day-one model ---------- */
export type SecteurId = 'salon' | 'institut' | 'restaurant' | 'cafe' | 'bar' | 'garage' | 'opticien' | 'boutique' | 'autre'
export const SECTEURS: { id: SecteurId; label: string; floor: number }[] = [
  { id: 'salon', label: 'Salon de coiffure', floor: 30 }, { id: 'institut', label: 'Institut / spa', floor: 30 },
  { id: 'restaurant', label: 'Restaurant', floor: 7 }, { id: 'cafe', label: 'Café / boulangerie', floor: 1 },
  { id: 'bar', label: 'Bar', floor: 7 }, { id: 'garage', label: 'Garage / auto', floor: 60 },
  { id: 'opticien', label: 'Opticien', floor: 60 }, { id: 'boutique', label: 'Boutique', floor: 7 }, { id: 'autre', label: 'Autre', floor: 7 },
]
const secteurKey = () => `bmr.secteur.${currentWorld()}`
/** The sector chosen on day one — null until the merchant answers « Votre secteur ? » (the mock's worlds are salons). With an
    API, the workspace's (GET /v1/app/workspace). */
export function getSecteur(): SecteurId | null {
  if (apiWrites) return mirror().workspace?.sector ?? null
  try { const v = localStorage.getItem(secteurKey()); if (v && SECTEURS.some(x => x.id === v)) return v as SecteurId } catch { /* fine */ }
  return null
}
export function setSecteur(id: SecteurId): void {
  if (apiWrites) { setWorkspace({ sector: id }); edited({ kind: 'workspace', fields: ['sector'] }); notify(); return }
  try { localStorage.setItem(secteurKey(), id) } catch { /* fine */ }; notify()
}
/** 2b-2 · with an API the workspace's settings change in this tab's copy, then lib/writes sends them (PATCH /v1/app/workspace). */
export function setWorkspace(patch: Partial<ServerWorkspace>): void {
  setMirror({ workspace: { sector: null, identityFields: IDENTITY_DEFAULT, wheelDesign: null, ...mirror().workspace, ...patch } })
}
export function useSecteur(): SecteurId | null { return useSyncExternalStore(subscribe, getSecteur, getSecteur) }
export const secteurLabel = (id: SecteurId | null) => SECTEURS.find(x => x.id === id)?.label ?? 'Salon de coiffure'
/** The floor of the window for the sector chosen — the salon's 30 days until a sector is chosen. */
export const sectorFloor = () => SECTEURS.find(x => x.id === getSecteur())?.floor ?? FENETRE_SECTEUR
export const FENETRE_DEFAULT = 30
export const FENETRE_MAX = 365
const fenetreKey = () => `bmr.fenetre.${currentWorld()}`
function readFenetres(): Record<string, number> {
  try { const raw = localStorage.getItem(fenetreKey()); if (raw) { const o = JSON.parse(raw); if (o && typeof o === 'object') return o } } catch { /* fine */ }
  return {}
}
/** The window of one establishment, in days (the sector's rhythm until the merchant sets it). */
export function getWindowDays(locationId: string): number {
  const v = apiWrites ? mirror().locations.find(l => l.id === serverLocationId(locationId))?.windowDays : readFenetres()[locationId]
  return typeof v === 'number' && v >= 1 ? v : Math.max(FENETRE_DEFAULT, sectorFloor())
}
/** Set it — clamped to [the sector's rhythm, a year]; the UI never offers less, the seam never stores less. */
export function setWindowDays(locationId: string, days: number): number {
  const d = Math.min(FENETRE_MAX, Math.max(sectorFloor(), Math.round(days)))
  /* with an API, the establishment's copy in this tab (GET /v1/app/locations), then the server's (PATCH …/locations/:id) */
  if (apiWrites) {
    const id = serverLocationId(locationId)
    setMirror({ locations: mirror().locations.map(l => l.id === id ? { ...l, windowDays: d } : l) })
    edited({ kind: 'location', id, fields: ['windowDays'] }); notify(); return d
  }
  const all = readFenetres(); all[locationId] = d
  try { localStorage.setItem(fenetreKey(), JSON.stringify(all)) } catch { /* fine */ }
  notify()
  return d
}
/** Reactive read for a screen. */
export function useWindowDays(locationId: string): number {
  return useSyncExternalStore(subscribe, () => getWindowDays(locationId), () => getWindowDays(locationId))
}
/** The window that applies in the current context: the establishment's, or the first one's from « Tous ». */
export function windowDaysInScope(all: Location[]): number {
  const s = getScope()
  const id = s === ALL ? (allowedLocations(all)[0] ?? all[0])?.id : s
  return id ? getWindowDays(id) : FENETRE_DEFAULT
}
/** « 1 par client / 30 jours » — the merchant's sentence for a window. */
export const windowSentence = (days: number) => `1 par client / ${days} jour${days > 1 ? 's' : ''}`

/* ---------- D-69 ① · the identity fields — set once in Établissement › Réglages (« Ce que le client renseigne ») ----------
   E-mail always (the gift has to reach someone), prénom on by default, téléphone off by default; the consent line is
   fixed. One value per world (one establishment = one setting; a network shares it until it needs more); with an API, the
   workspace's (02 §2.3), like the sector. */
export interface IdentityFields { prenom: boolean; telephone: boolean }
export const IDENTITY_DEFAULT: IdentityFields = { prenom: true, telephone: false }
const identKey = () => `bmr.identite-champs.${currentWorld()}`
let identCache: { key: string; raw: string | null; value: IdentityFields } | null = null
/** Cached per (world, stored string) so useSyncExternalStore gets a stable snapshot. */
export function getIdentityFields(): IdentityFields {
  if (apiWrites) return mirror().workspace?.identityFields ?? IDENTITY_DEFAULT
  const key = identKey(); let raw: string | null = null
  try { raw = localStorage.getItem(key) } catch { /* fine */ }
  if (identCache && identCache.key === key && identCache.raw === raw) return identCache.value
  let value: IdentityFields = IDENTITY_DEFAULT
  try { if (raw) { const o = JSON.parse(raw); if (o && typeof o === 'object') value = { ...IDENTITY_DEFAULT, ...o } } } catch { /* fine */ }
  identCache = { key, raw, value }
  return value
}
export function setIdentityFields(f: Partial<IdentityFields>): IdentityFields {
  const next = { ...getIdentityFields(), ...f }
  if (apiWrites) { setWorkspace({ identityFields: next }); edited({ kind: 'workspace', fields: ['identityFields'] }); notify(); return next }
  try { localStorage.setItem(identKey(), JSON.stringify(next)) } catch { /* fine */ }
  notify(); return getIdentityFields()
}
export function useIdentityFields(): IdentityFields { return useSyncExternalStore(subscribe, getIdentityFields, getIdentityFields) }
/** The merchant's sentence: « e-mail · prénom » */
export const identitySentence = (f: IdentityFields) => ['e-mail', f.prenom ? 'prénom' : null, f.telephone ? 'téléphone' : null].filter(Boolean).join(' · ')
